import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { Container, Button } from "@bw/bits"
import { motion } from "framer-motion"
import { breakpoints } from "../../../theme"

const variants = {
  open: { opacity: 1, display: "flex", justifyContent: "flex-end" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const innerVariants = {
  open: { opacity: 1, display: "flex" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const Layer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9;
`

const InnerLayer = styled(motion.div)`
  background-color: #111;
  width: 100%;
  margin: auto;
  padding: 0;
  aspect-ratio: 16 / 9;
  width: 90vw;
  max-width: 800px;
`

const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: calc(40px + var(--logo-offset)) 0 100px;
  color: var(--hero-text-color, #fff);

  @media (min-width: ${breakpoints.medium}px) {
    min-height: ${props => (props.fullPage ? "100vh" : "520px")};
    padding: calc(60px + var(--logo-offset)) 0 100px;
  }
`

const Image = styled.div`
  position: absolute;
  overflow: hidden;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  clip-path: var(
    --hero-clip-path-mobile,
    polygon(0 0, 100% 0, 100% 100%, 50% calc(100% - 20px), 0 100%)
  );

  @media (min-width: ${breakpoints.medium}px) {
    clip-path: var(
      --hero-clip-path-desktop,
      polygon(0 0, 100% 0, 100% 100%, 50% calc(100% - 45px), 0 100%)
    );
  }

  &:after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background: var(
      --hero-overlay-background,
      linear-gradient(
        266.35deg,
        rgba(14, 20, 0, 0) 0%,
        rgba(14, 20, 0, 0.2) 40%,
        rgba(14, 20, 0, 0.4) 60%,
        rgba(14, 20, 0, 0.6) 100%
      )
    );
  }

  .gatsby-image-wrapper {
    height: 100%;

    margin: var(--hero-image-margin-mobile, 0);

    @media (min-width: ${breakpoints.medium}px) {
      margin: var(--hero-image-margin-desktop, 0);
    }
  }

  img {
    object-position: ${props => props.focus};
  }
`

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledHeading = styled.div`
  max-width: 640px;
  line-height: 1.2;
  color: currentColor;
`

const Suptitle = styled.div`
  font-weight: 500;
  font-size: 22px;
  line-height: 1.1;
  margin-bottom: 0.3em;

  @media (min-width: ${breakpoints.small}px) {
    font-size: 22px;
  }
`

const Title = styled.h1`
  margin: 0 0 0.4em;
  line-height: 1;
  font-weight: 700;
  font-size: 36px;

  &:last-child {
    margin: 0;
  }

  @media (min-width: ${breakpoints.small}px) {
    font-size: 45px;
  }
`

const DivTitle = styled.div`
  margin: 0 0 0.4em;
  line-height: 1;
  font-weight: 700;
  font-size: 36px;

  &:last-child {
    margin: 0;
  }

  @media (min-width: ${breakpoints.small}px) {
    font-size: 45px;
  }
`

const Subtitle = styled.div`
  max-width: 445px;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.6;

  @media (min-width: ${breakpoints.small}px) {
    font-size: 20px;
  }
`

const Buttons = styled.div`
  max-width: 100%;
  justify-content: ${props => (props.justify ? props.justify : "")};
  margin: 20px 0 0 -10px;

  > * {
    margin: 10px 10px 0;
    font-size: 15px;
    min-width: var(--header-button-min-width, 165px);
  }
`

const Hero = ({
  suptitle,
  title,
  subtitle,
  containerSize,
  buttons,
  buttonsPosition,
  image,
  focus,
  video,
  videoPopup,
  fullPage,
  noH1Tag,
}) => {
  const [show, setShow] = React.useState(false)
  const { t } = useTranslation()
  const heroRef = React.useRef(null)
  const layerRef = React.useRef(null)
  const videoRef = React.useRef(null)

  const closeOnOutsideClick = React.useCallback(
    (e, ref) => {
      if (e.target === ref.current) {
        setShow(false)
      }
    },
    [setShow]
  )

  React.useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27) {
        setShow(false)
      }
    }

    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [])

  React.useEffect(() => {
    if (videoPopup && videoRef.current) {
      if (show) {
        videoRef.current.play()
      } else {
        videoRef.current.pause()
      }
    }
  }, [videoPopup, videoRef, show])

  const titleTag = noH1Tag ? (
    <DivTitle className={`pageheader__maintitle`}>{title}</DivTitle>
  ) : (
    <Title className={`pageheader__maintitle`}>{title}</Title>
  )

  return (
    <>
      <HeroWrapper ref={heroRef} fullPage={fullPage} className={`hero`}>
        <Image focus={focus} className={`hero__image`}>
          {image}
        </Image>
        {video && (
          <Video autoPlay muted loop className={`hero__video`}>
            <source src={video} type="video/mp4" />
          </Video>
        )}
        <Container size={containerSize} className={`hero__container`}>
          <StyledHeading className={`hero__title`}>
            {suptitle && (
              <Suptitle className={`pageheader__suptitle`}>{suptitle}</Suptitle>
            )}
            {titleTag}
            {subtitle && (
              <Subtitle className={`pageheader__subtitle`}>{subtitle}</Subtitle>
            )}
          </StyledHeading>
          {(buttons.length > 0 || videoPopup) && (
            <Buttons justify={buttonsPosition} className={`hero__buttons`}>
              {buttons}
              {videoPopup && (
                <Button
                  primary
                  onClick={() => setShow(true)}
                  inversed
                  label={"▶ " + t("hero.playVideo")}
                />
              )}
            </Buttons>
          )}
        </Container>
      </HeroWrapper>

      {videoPopup && (
        <Layer
          animate={show ? "open" : "closed"}
          variants={variants}
          transition={{ duration: 0.4 }}
          initial={false}
          ref={layerRef}
          onClick={e => closeOnOutsideClick(e, layerRef)}
        >
          <InnerLayer
            animate={show ? "open" : "closed"}
            variants={innerVariants}
            transition={{ duration: 0.6 }}
            initial={false}
          >
            <video controls autoPlay ref={videoRef}>
              <source src={videoPopup} type="video/mp4" />
            </video>
          </InnerLayer>
        </Layer>
      )}
    </>
  )
}

Hero.propTypes = {
  suptitle: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.element),
  image: PropTypes.element.isRequired,
  video: PropTypes.string,
  videoPopup: PropTypes.string,
  buttonsPosition: PropTypes.oneOf(["start", "center", "end"]),
  containerSize: PropTypes.oneOf(["small", "medium", "large"]),
  focus: PropTypes.string,
  fullPage: PropTypes.bool,
  noH1Tag: PropTypes.bool,
}

Hero.defaultProps = {
  buttonsPosition: "start",
  containerSize: "medium",
  focus: "80% center",
  fullPage: false,
  noH1Tag: false,
}

export { Hero }
